// src/tabs/ControleStatsSection.jsx — LOT STATS du contrôle des ménages.
//
// Section repliée « Contrôle » montée EN BAS de l'onglet Opérations → Ménages
// (MenagesOpsTab — ⚠️ ADMIN SEUL : l'onglet hôte est admin-only et l'action
// serveur `controle_stats` est routée requireAdmin ; le proprio ne voit jamais
// rien du contrôle). Même motif que la SectionHistorique de l'écran Demandes
// (src/views/OrchestrateurHorairesView.jsx) :
//   · FERMÉE par défaut, et elle ne charge RIEN avant d'être ouverte ;
//   · un mois = une requête, mise en cache pour la session de l'écran ;
//   · sélecteur ‹ › borné [premier_mois (servi par le serveur), mois courant].
//
// Contenu : compteurs en tête (contrôles / rien à signaler / avec points /
// repris / en attente + délai moyen de reprise quand il existe), puis deux
// tables « Par logement » (nom en clair via cfg, repli « Logement » — jamais
// un identifiant technique nu) et « Par agent » (prénom seul, servi tel quel
// par le serveur — jamais d'email). Aucune PII voyageur, aucun montant.

import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../supabase'
import { DS } from '../lib/ui'
import { AlertTriangle } from 'lucide-react'

const ENDPOINT = '/api/beds24-mapping'

// POST { action, ... } avec Bearer JWT — même pattern que src/lib/controle-api.js
// (non importé : ce module-là ne l'exporte pas, et il appartient au chantier
// mobile du contrôleur, en vol chez un autre agent).
async function postAction(body) {
  let res
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    })
  } catch (e) {
    throw new Error('Connexion impossible. Réessayez.')
  }
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.error || `Erreur ${res.status}`)
  return json
}

// Mois courant 'YYYY-MM' Europe/Paris (fr-CA rend YYYY-MM-DD).
function moisCourantParis() {
  return new Intl.DateTimeFormat('fr-CA', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date()).slice(0, 7)
}

// « 2026-08 » → « août 2026 » ; mois voisin en calendrier civil.
function libelleMois(mois) {
  try {
    return new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' })
      .format(new Date(`${mois}-01T12:00:00`))
  } catch (_) { return mois }
}
function moisVoisin(mois, delta) {
  const [a, m] = mois.split('-').map(Number)
  const total = a * 12 + (m - 1) + delta
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`
}

// « 5.2 » → « 5,2 h » — décimale française, jamais un point à l'écran.
function libelleHeures(h) {
  return `${String(h).replace('.', ',')} h`
}

// Nom de logement en clair via cfg (logs + chambres sous-loc — même parcours
// que findLog de MenagesOpsTab). Repli « Logement » : jamais d'id technique nu.
function nomLogement(cfg, logementId) {
  if (cfg && logementId) {
    for (const prop of cfg.props || []) {
      for (const log of prop.logs || []) {
        if (log.id === logementId) return log.nom || 'Logement'
        for (const ch of log.chambres || []) {
          if (ch.id === logementId) return ch.nom || 'Logement'
        }
      }
    }
  }
  return 'Logement'
}

const s = (n) => (n > 1 ? 's' : '')

// Petite table Contrôlés / Bons / Points / Repris — le conteneur scrolle
// horizontalement si l'écran est étroit, la page ne déborde jamais.
function TableStats({ titre, lignes, libelleLigne, testid }) {
  const th = {
    textAlign: 'right', padding: '6px 10px', fontSize: 10.5, fontWeight: 700,
    color: DS.textMuted, textTransform: 'uppercase', letterSpacing: '.06em',
    whiteSpace: 'nowrap',
  }
  const td = { textAlign: 'right', padding: '7px 10px', fontSize: 12.5, color: DS.marineDark, whiteSpace: 'nowrap' }
  return (
    <div style={{ flex: '1 1 280px', minWidth: 0 }}>
      <div style={{ fontFamily: DS.fontDisplay, fontSize: 13.5, fontWeight: 600, color: DS.marine, marginBottom: 6 }}>
        {titre}
      </div>
      <div style={{ overflowX: 'auto', background: DS.card, border: `0.5px solid ${DS.border}`, borderRadius: 10 }}>
        <table data-testid={testid} style={{ borderCollapse: 'collapse', width: '100%', minWidth: 320 }}>
          <thead>
            <tr style={{ borderBottom: `0.5px solid ${DS.border}` }}>
              <th style={{ ...th, textAlign: 'left' }}>{titre === 'Par agent' ? 'Agent' : 'Logement'}</th>
              <th style={th}>Contrôlés</th>
              <th style={th}>Bons</th>
              <th style={th}>Points</th>
              <th style={th}>Repris</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((l, i) => (
              <tr key={i} style={{ borderBottom: i < lignes.length - 1 ? `0.5px solid ${DS.border}` : 'none' }}>
                <td style={{ ...td, textAlign: 'left', fontWeight: 600, color: DS.marine }}>{libelleLigne(l)}</td>
                <td style={td}>{l.controles}</td>
                <td style={td}>{l.bons}</td>
                <td style={td}>{l.avec_points}</td>
                <td style={td}>{l.repris}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function ControleStatsSection({ cfg }) {
  const moisCourant = useMemo(() => moisCourantParis(), [])
  const [ouvert, setOuvert] = useState(false)
  const [mois, setMois] = useState(moisCourant)
  const [chargement, setChargement] = useState(false)
  const [erreur, setErreur] = useState(null)
  const [parMois, setParMois] = useState({}) // { 'YYYY-MM': réponse serveur }
  const [tentative, setTentative] = useState(0) // « Réessayer » relance le fetch

  const donnees = parMois[mois] || null
  // La borne basse vient de N'IMPORTE QUELLE réponse déjà reçue (le serveur la
  // répète) — sans réponse, on ne sait pas et on ne borne que le futur.
  const premierMois = Object.values(parMois).map((d) => d.premier_mois).find(Boolean) || null

  // RIEN ne part tant que la section est fermée — même motif SectionHistorique.
  useEffect(() => {
    if (!ouvert || parMois[mois]) return
    let annule = false
    setChargement(true)
    setErreur(null)
    postAction({ action: 'controle_stats', month: mois })
      .then((json) => { if (!annule) setParMois((prev) => ({ ...prev, [mois]: json })) })
      .catch((e) => {
        console.error('[controle-stats] chargement', e?.message || e)
        if (!annule) setErreur('Les statistiques n’ont pas pu être chargées.')
      })
      .finally(() => { if (!annule) setChargement(false) })
    return () => { annule = true }
  }, [ouvert, mois, parMois, tentative])

  const totaux = donnees?.totaux || null
  const delai = donnees?.delai_reprise_moyen_heures
  const precDesactive = chargement || (premierMois != null && mois <= premierMois)
  const suivDesactive = chargement || mois >= moisCourant

  const boutonMois = (desactive) => ({
    minWidth: 34, minHeight: 34, borderRadius: 8, border: `0.5px solid ${DS.border}`,
    background: DS.card, color: desactive ? DS.textMuted : DS.marine,
    fontSize: 15, cursor: desactive ? 'default' : 'pointer', opacity: desactive ? 0.45 : 1,
  })

  return (
    <section style={{ marginTop: 26, fontFamily: DS.fontBody }}>
      <button type="button" data-testid="controle-stats-toggle"
        onClick={() => setOuvert((o) => !o)} className="fbz-press"
        aria-expanded={ouvert}
        style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'none',
          border: 'none', padding: '6px 0', cursor: 'pointer', fontFamily: DS.fontBody,
          fontSize: 10.5, fontWeight: 700, color: DS.textMuted, textTransform: 'uppercase',
          letterSpacing: '.09em' }}>
        <span aria-hidden="true" style={{ display: 'inline-block', fontSize: 9,
          transform: ouvert ? 'rotate(90deg)' : 'none', transition: 'transform .12s ease' }}>▶</span>
        Contrôle
      </button>

      {ouvert && (
        <div data-testid="controle-stats">
          {/* Le sélecteur de mois — borné [premier contrôle, mois courant]. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '6px 0 10px' }}>
            <button type="button" data-testid="controle-stats-mois-prec"
              onClick={() => setMois((m) => moisVoisin(m, -1))}
              disabled={precDesactive} aria-label="Mois précédent"
              className="fbz-press" style={boutonMois(precDesactive)}>‹</button>
            <span data-testid="controle-stats-mois" style={{ fontFamily: DS.fontDisplay,
              fontSize: 14.5, fontWeight: 600, color: DS.marine, minWidth: 130, textAlign: 'center' }}>
              {libelleMois(mois)}
            </span>
            <button type="button" data-testid="controle-stats-mois-suiv"
              onClick={() => setMois((m) => moisVoisin(m, 1))}
              disabled={suivDesactive} aria-label="Mois suivant"
              className="fbz-press" style={boutonMois(suivDesactive)}>›</button>
          </div>

          {chargement && <div style={{ color: DS.textMuted, fontSize: 13 }}>Chargement…</div>}

          {!chargement && erreur && (
            <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap',
              background: DS.dangerBg, color: DS.dangerText, borderRadius: 9,
              padding: '10px 14px', fontSize: 13 }}>
              <AlertTriangle size={15} strokeWidth={2} style={{ flexShrink: 0 }} />
              {erreur}
              <button type="button" data-testid="controle-stats-reessayer"
                onClick={() => setTentative((t) => t + 1)} className="fbz-press"
                style={{ marginLeft: 'auto', border: `0.5px solid ${DS.dangerText}`,
                  background: 'transparent', color: DS.dangerText, borderRadius: 8,
                  padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                Réessayer
              </button>
            </div>
          )}

          {!chargement && !erreur && donnees && totaux?.controles === 0 && (
            <div data-testid="controle-stats-vide" style={{ color: DS.textMuted, fontSize: 13, padding: '4px 0 8px' }}>
              Aucun contrôle en {libelleMois(mois)}.
            </div>
          )}

          {!chargement && !erreur && donnees && totaux?.controles > 0 && (
            <>
              {/* LES COMPTEURS EN TÊTE — la phrase se lit d'un coup d'œil. */}
              <div data-testid="controle-stats-compteurs"
                style={{ fontSize: 12.5, color: DS.textSecondary, marginBottom: 12, lineHeight: 1.55 }}>
                <strong style={{ color: DS.marine }}>{totaux.controles} contrôle{s(totaux.controles)}</strong>
                {' · '}{totaux.bons} rien à signaler
                {' · '}{totaux.avec_points} avec point{s(totaux.avec_points)}
                {' · '}{totaux.repris} repris
                {/* 2026-08-19 : les reprises SUR PLACE (« je reprends
                    maintenant » naît clos) se disent à part — un délai de 0 h
                    n'est pas un délai. */}
                {totaux.repris_sur_place > 0 && (
                  <span data-testid="controle-stats-sur-place">
                    {' '}(dont {totaux.repris_sur_place} sur place)
                  </span>
                )}
                {' · '}{totaux.en_attente} en attente
                {typeof delai === 'number' && (
                  <span style={{ color: DS.textMuted }}>
                    {' — '}délai moyen de reprise par l'agent : {libelleHeures(delai)}
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <TableStats titre="Par logement" testid="controle-stats-par-logement"
                  lignes={donnees.par_logement || []}
                  libelleLigne={(l) => nomLogement(cfg, l.logement_id)} />
                <TableStats titre="Par agent" testid="controle-stats-par-agent"
                  lignes={donnees.par_agent || []}
                  libelleLigne={(l) => l.agent_prenom || 'Agent'} />
              </div>
            </>
          )}
        </div>
      )}
    </section>
  )
}
